// src/components/Admin/EpisodeFormModal.tsx
import React, { useState, useEffect } from 'react';
import { X, Save, Search } from 'lucide-react';
import type { Episode } from '../../types/series';
import type { ContentItem } from '../../services/adminService';
import seriesService from '../../services/seriesService';
import adminService from '../../services/adminService';
import { useToast } from '../../hooks/useToast';

interface EpisodeFormModalProps {
  isOpen: boolean;
  mode: 'add' | 'edit' | 'view';
  episode?: Episode;
  seriesId?: string;
  seasonId?: string;
  onClose: () => void;
  onSave: (episode: Episode) => void;
}

const EpisodeFormModal: React.FC<EpisodeFormModalProps> = ({
  isOpen,
  mode,
  episode,
  seriesId,
  seasonId,
  onClose,
  onSave,
}) => {
  const { showError, showSuccess } = useToast();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    episodeNumber: 1,
    contentId: '',
    airDate: '',
    status: 'DRAFT' as 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'SCHEDULED',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availableContent, setAvailableContent] = useState<ContentItem[]>([]);
  const [contentSearch, setContentSearch] = useState('');
  const [loadingContent, setLoadingContent] = useState(false);

  // Initialize form data when modal opens or episode changes
  useEffect(() => {
    if (episode && (mode === 'edit' || mode === 'view')) {
      setFormData({
        title: episode.title || '',
        description: episode.description || '',
        episodeNumber: episode.episodeNumber || 1,
        contentId: episode.contentId || '',
        airDate: episode.airDate ? episode.airDate.split('T')[0] : '', // Convert to date input format
        status: episode.status,
      });
    } else if (mode === 'add') {
      setFormData({
        title: '',
        description: '',
        episodeNumber: 1,
        contentId: '',
        airDate: '',
        status: 'DRAFT',
      });
    }
    setErrors({});
  }, [episode, mode, isOpen]);

  const loadAvailableContent = React.useCallback(async () => {
    try {
      setLoadingContent(true);
      const response = await adminService.getContentManagement({
        search: contentSearch,
        page_size: 50, // Load more content for selection
      });
      setAvailableContent(response.content || []);
    } catch (error) {
      console.error('Error loading content:', error);
      setAvailableContent([]);
    } finally {
      setLoadingContent(false);
    }
  }, [contentSearch]);

  // Load available content when modal opens
  useEffect(() => {
    if (isOpen && mode === 'add') {
      loadAvailableContent();
    }
  }, [isOpen, mode, loadAvailableContent]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleContentSearch = (searchTerm: string) => {
    setContentSearch(searchTerm);
    // Debounce search - in a real app, you'd want to debounce this
    setTimeout(() => {
      loadAvailableContent();
    }, 300);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.episodeNumber || formData.episodeNumber < 1) {
      newErrors.episodeNumber = 'Episode number must be 1 or greater';
    }

    if (!formData.contentId && mode === 'add') {
      newErrors.contentId = 'Content selection is required';
    }

    if (!seriesId && mode === 'add') {
      newErrors.general = 'Series ID is required to create an episode';
    }

    if (!seasonId && mode === 'add') {
      newErrors.general = 'Season ID is required to create an episode';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'view') return;

    if (!validateForm()) {
      return;
    }

    if (!seriesId || !seasonId) {
      showError('Series and Season IDs are required');
      return;
    }

    setIsLoading(true);
    try {
      let savedEpisode: Episode;
      
      if (mode === 'add') {
        savedEpisode = await seriesService.createEpisode(seriesId, seasonId, formData);
        showSuccess(`Episode "${formData.title}" created successfully`);
      } else if (mode === 'edit' && episode) {
        savedEpisode = await seriesService.updateEpisode(seriesId, seasonId, episode.id, formData);
        showSuccess(`Episode "${formData.title}" updated successfully`);
      } else {
        throw new Error('Invalid mode or missing episode data');
      }

      onSave(savedEpisode);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save episode';
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const statusOptions = [
    { value: 'DRAFT', label: 'Draft', description: 'Not published yet' },
    { value: 'PUBLISHED', label: 'Published', description: 'Live and visible to users' },
    { value: 'ARCHIVED', label: 'Archived', description: 'Hidden from public view' },
    { value: 'SCHEDULED', label: 'Scheduled', description: 'Scheduled for future release' },
  ];

  const getModalTitle = () => {
    switch (mode) {
      case 'add':
        return 'Create New Episode';
      case 'edit':
        return `Edit: ${episode?.title || 'Episode'}`;
      case 'view':
        return `View: ${episode?.title || 'Episode'}`;
      default:
        return 'Episode Form';
    }
  };

  const selectedContent = availableContent.find(content => content.id === formData.contentId);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {getModalTitle()}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              disabled={mode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter episode title"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={mode === 'view'}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter episode description"
            />
          </div>

          {/* Episode Number and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Episode Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Episode Number *
              </label>
              <input
                type="number"
                min="1"
                value={formData.episodeNumber}
                onChange={(e) => handleInputChange('episodeNumber', parseInt(e.target.value) || 1)}
                disabled={mode === 'view'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
                placeholder="1"
              />
              {errors.episodeNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.episodeNumber}</p>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                disabled={mode === 'view'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {statusOptions.find(opt => opt.value === formData.status)?.description}
              </p>
            </div>
          </div>

          {/* Content Selection - Only for add mode */}
          {mode === 'add' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Content *
              </label>
              <div className="space-y-3">
                {/* Search Content */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search available content..."
                    value={contentSearch}
                    onChange={(e) => handleContentSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                {/* Content Selection */}
                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg">
                  {loadingContent ? (
                    <div className="p-4 text-center text-gray-500">
                      Loading content...
                    </div>
                  ) : availableContent.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      No content available
                    </div>
                  ) : (
                    <div className="space-y-1 p-2">
                      {availableContent.map((content) => (
                        <label
                          key={content.id}
                          className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${
                            formData.contentId === content.id ? 'bg-orange-50 border border-orange-200' : ''
                          }`}
                        >
                          <input
                            type="radio"
                            name="contentId"
                            value={content.id}
                            checked={formData.contentId === content.id}
                            onChange={(e) => handleInputChange('contentId', e.target.value)}
                            className="mr-3 text-orange-600 focus:ring-orange-500"
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{content.title}</div>
                            <div className="text-xs text-gray-500">
                              {content.contentType} • by {content.author.username}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {selectedContent && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-700">
                      Selected: <strong>{selectedContent.title}</strong>
                    </p>
                  </div>
                )}

                {errors.contentId && (
                  <p className="text-sm text-red-600">{errors.contentId}</p>
                )}
              </div>
            </div>
          )}

          {/* Air Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Air Date
            </label>
            <input
              type="date"
              value={formData.airDate}
              onChange={(e) => handleInputChange('airDate', e.target.value)}
              disabled={mode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
            />
            <p className="mt-1 text-sm text-gray-500">
              Optional: Set when this episode will air
            </p>
          </div>

          {/* Show existing episode info for view/edit mode */}
          {(mode === 'edit' || mode === 'view') && episode && (
            <div className="bg-gray-50 rounded-lg p-4 border-t">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Episode Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Duration: </span>
                  <span className="text-gray-600">
                    {Math.floor(episode.durationSeconds / 60)}m {episode.durationSeconds % 60}s
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Views: </span>
                  <span className="text-gray-600">{episode.viewCount?.toLocaleString() || '0'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Likes: </span>
                  <span className="text-gray-600">{episode.likeCount?.toLocaleString() || '0'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Created: </span>
                  <span className="text-gray-600">
                    {new Date(episode.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
            >
              {mode === 'view' ? 'Close' : 'Cancel'}
            </button>
            {mode !== 'view' && (
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isLoading || !formData.title.trim() || (mode === 'add' && !formData.contentId)}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:ring-2 focus:ring-orange-500 disabled:opacity-50 flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {mode === 'add' ? 'Create Episode' : 'Save Changes'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EpisodeFormModal;